// Meta Pixel from our proxy; the caller loads it for Pfida only. The stub queues calls with consent revoked;
// Meta's script is fetched only on an advertising yes (Legal, 5 October 2026).

import { injectScript } from '../injectScript';
import { isPublicPath } from '../publicPages';

export type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push?: Fbq;
  loaded?: boolean;
  version?: string;
  // read by Meta's script: true stops its own PageView on every address change
  disablePushState?: boolean;
};

export type MetaPixelWindow = Window & {
  fbq?: Fbq;
  _fbq?: Fbq;
  pfMetaPixelLoaded?: boolean;
  // Grant waits for Meta's script; a revoke always applies at once
  pfMetaPixelScriptReady?: boolean;
  pfMetaPixelGrantOnLoad?: boolean;
  // set until the script is injected on the first yes
  pfMetaPixelScriptUrl?: string;
};

type MetaPixelSettings = {
  pixelId?: string;
  scriptUrl?: string;
  pathname?: string;
};

function installStub(): Fbq {
  const win = window as MetaPixelWindow;
  // Meta's standard base code: queue calls until fbevents.js takes over
  const fbq = function stub(...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      fbq.queue.push(args);
    }
  } as Fbq;
  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  win.fbq = fbq;
  if (!win._fbq) {
    win._fbq = fbq;
  }
  return fbq;
}

function loadMetaScript(): void {
  const win = window as MetaPixelWindow;
  const scriptUrl = win.pfMetaPixelScriptUrl;
  if (!scriptUrl) {
    return;
  }
  win.pfMetaPixelScriptUrl = undefined;
  const script = injectScript(scriptUrl);
  script.addEventListener('load', () => {
    win.pfMetaPixelScriptReady = true;
    if (win.pfMetaPixelGrantOnLoad) {
      win.pfMetaPixelGrantOnLoad = false;
      win.fbq?.('consent', 'grant');
    }
  });
}

/**
 * Returns true when this call loaded the Pixel. Does nothing on a signed in page (Legal: no
 * tracking script on pages with account or investment details), when a build setting is missing,
 * or when already loaded; never throws.
 */
export function loadMetaPixel({
  pixelId,
  scriptUrl,
  pathname = window.location.pathname,
}: MetaPixelSettings): boolean {
  try {
    const win = window as MetaPixelWindow;
    if (!pixelId || !scriptUrl || win.pfMetaPixelLoaded || !isPublicPath(pathname)) {
      return false;
    }
    win.pfMetaPixelLoaded = true;

    const fbq = installStub();
    // one PageView for the page it loaded on; later pages are not reported
    fbq.disablePushState = true;

    fbq('consent', 'revoke');
    fbq('init', pixelId);
    fbq('track', 'PageView');

    win.pfMetaPixelScriptUrl = scriptUrl;
    return true;
  } catch {
    return false;
  }
}

/**
 * Apply the visitor's advertising choice. Revoke is immediate; a grant made
 * before Meta's script has loaded is held and applied on load. Never throws.
 */
export function setMetaPixelConsent(granted: boolean): void {
  try {
    const win = window as MetaPixelWindow;
    if (!win.pfMetaPixelLoaded || !win.fbq) {
      return;
    }
    if (!granted) {
      win.pfMetaPixelGrantOnLoad = false;
      win.fbq('consent', 'revoke');
      return;
    }
    if (win.pfMetaPixelScriptReady) {
      win.fbq('consent', 'grant');
    } else {
      win.pfMetaPixelGrantOnLoad = true;
      loadMetaScript();
    }
  } catch {
    // consent must never break the app
  }
}
